import * as Sentry from '@sentry/angular';
import { SENTRY_TRACE_PROBE_NAME } from './sentry-trace-policy';

export interface SentryTraceProbeResult {
  traceId: string;
  flushCompleted: boolean;
  statusCode?: number;
}

export const SENTRY_TRACE_PROBE_CLIENT_GUID =
  '00000000-0000-4000-8000-000000000003';
export const SENTRY_TRACE_PROBE_IDS = {
  client_guid: SENTRY_TRACE_PROBE_CLIENT_GUID,
  case_id: '00000000-0000-4000-8000-000000000004',
  conversation_id: 'synthetic-conversation-1',
  run_id: 'synthetic-run-1',
};

/** Emit synthetic spans only. The HTTP-labelled child does not make a request. */
export async function runSentryTraceProbe(
  runId: string,
): Promise<SentryTraceProbeResult> {
  let traceId = '';
  let statusCode: number | undefined;
  const client = Sentry.getClient();
  const stop = client?.on('afterSendEvent', (event, response) => {
    if (
      event.type === 'transaction' &&
      event.tags?.['source'] === 'sentry_trace_test' &&
      event.tags?.['test_run_id'] === runId &&
      event.contexts?.trace?.trace_id === traceId
    )
      statusCode = response?.statusCode;
  });
  try {
    Sentry.withScope((scope) => {
      scope.setTag('source', 'sentry_trace_test');
      scope.setTag('test_run_id', runId);
      scope.setExtra('client_content', 'SENTRY_SYNTHETIC_PRIVATE_TRACE');
      scope.setExtra('account_number', 'SENTRY_SYNTHETIC_PRIVATE_TRACE');
      for (const [key, value] of Object.entries(SENTRY_TRACE_PROBE_IDS))
        scope.setExtra(key, value);
      Sentry.startSpan(
        {
          name: SENTRY_TRACE_PROBE_NAME,
          op: 'app.operation',
          forceTransaction: true,
        },
        (span) => {
          traceId = span.spanContext().traceId;
          span.setAttribute('client_content', 'SENTRY_SYNTHETIC_PRIVATE_TRACE');
          span.setAttribute('account_number', 'SENTRY_SYNTHETIC_PRIVATE_TRACE');
          span.setAttributes(SENTRY_TRACE_PROBE_IDS);
          Sentry.startSpan(
            {
              name: 'GET https://example.invalid/case/00000000-0000-4000-8000-000000000004?q=SENTRY_SYNTHETIC_PRIVATE_TRACE',
              op: 'http.client',
              attributes: {
                'http.request.method': 'GET',
                ...SENTRY_TRACE_PROBE_IDS,
                'http.response.status_code': 503,
                'url.full':
                  'https://example.invalid/case/00000000-0000-4000-8000-000000000004?q=SENTRY_SYNTHETIC_PRIVATE_TRACE',
                'response.body': 'SENTRY_SYNTHETIC_PRIVATE_TRACE',
                account_number: 'SENTRY_SYNTHETIC_PRIVATE_TRACE',
              },
            },
            (child) => child.setStatus({ code: 2, message: 'unavailable' }),
          );
        },
      );
    });
    const flushCompleted = await Sentry.flush(5000);
    return { traceId, flushCompleted, statusCode };
  } finally {
    stop?.();
  }
}
